/**
 * Gráfica de "Pedidos de láminas recibidos" por período (presentacional + su hook).
 *
 * Réplica de la gráfica de pedidos del SSR (`advanced_stats_with_charts.html`,
 * `loadPedidosChart`): la serie de pedidos de láminas 'recibido' por día/semana/mes.
 * Gestiona su período y su query (`usePedidosLaminas`, cacheada por sede + período).
 *
 * El endpoint exige `view_pedidos_laminas` en el backend (distinto del resto de
 * analytics, que exigen `view_orders`): la pantalla solo monta esta gráfica con ese
 * permiso, así que no se recibe un 403 por un dato del que el resto no depende.
 */
import { useState } from "react";

import { DEFAULT_PEDIDOS_PERIOD, PEDIDOS_PERIOD_OPTIONS } from "../constants";
import { formatPeriodLabel, sortByPeriodAsc } from "../format";
import { usePedidosLaminas } from "../hooks/useStats";
import type { OrdersPeriod } from "../schemas";
import type { PedidosLaminasStats } from "../types";
import { BarChart } from "./BarChart";
import { ChartCard } from "./ChartCard";
import { PeriodTabs } from "./PeriodTabs";

interface PedidosLaminasChartProps {
  enabled: boolean;
}

export function PedidosLaminasChart({ enabled }: PedidosLaminasChartProps) {
  const [period, setPeriod] = useState<OrdersPeriod>(DEFAULT_PEDIDOS_PERIOD);
  const query = usePedidosLaminas(period, enabled);

  return (
    <ChartCard<PedidosLaminasStats>
      title="Pedidos de láminas recibidos"
      action={
        <PeriodTabs
          value={period}
          options={PEDIDOS_PERIOD_OPTIONS}
          onChange={setPeriod}
          ariaLabel="Período de pedidos de láminas"
        />
      }
      isPending={query.isPending}
      isError={query.isError}
      error={query.error}
      data={query.data}
    >
      {(stats) => (
        <BarChart
          barClassName="bg-emerald-500"
          data={sortByPeriodAsc(stats.data).map((point) => ({
            label: formatPeriodLabel(point.period, period),
            value: point.count,
          }))}
          emptyMessage="Sin pedidos recibidos en el período."
        />
      )}
    </ChartCard>
  );
}
